import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { 
  ensureAudioResumed, 
  createTeaCeremonyMusic, 
  stopTeaCeremonyMusic, 
  playPourSound 
} from './audioUtils';

const TeaCeremony = ({ audioCtxRef, soundEnabled }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const bgImageRef = useRef(null);
  const teapotImageRef = useRef(null);
  const musicRef = useRef(null);
  const [bgLoaded, setBgLoaded] = useState(false);
  
  // Teapot animation controller
  const teapotControls = useAnimation();
  
  // Cup state
  const [cups, setCups] = useState([
    { id: 1, filled: false, fillLevel: 0 },
    { id: 2, filled: false, fillLevel: 0 },
    { id: 3, filled: false, fillLevel: 0 },
  ]);
  const cupsRef = useRef(cups);
  
  // Animation state
  const [ceremonyState, setCeremonyState] = useState('idle'); // idle, lifting, pouring, returning
  const [activeCup, setActiveCup] = useState(null);
  const [complete, setComplete] = useState(false);
  const frameRef = useRef(0);
  const animFrameRef = useRef(null);
  const petalRef = useRef([]);

  // Positions as percentages of canvas (600x420)
  // Teapot resting position (center of table)
  const teapotHome = { x: 50, y: 82 };
  
  // Cup positions on the table
  const cupPositions = [
    { id: 1, x: 42, y: 90, label: 'Left cup' },
    { id: 2, x: 58, y: 90, label: 'Right cup' },
    { id: 3, x: 50, y: 93, label: 'Center cup' },
  ];

  // Sync refs
  useEffect(() => { cupsRef.current = cups; }, [cups]);

  // Music setup
  useEffect(() => {
    const audioCtx = audioCtxRef?.current;
    if (!soundEnabled || !audioCtx) return;
    const startMusic = async () => {
      const isReady = await ensureAudioResumed(audioCtx);
      if (isReady) musicRef.current = createTeaCeremonyMusic(audioCtx);
    };
    startMusic();
    return () => {
      if (musicRef.current) stopTeaCeremonyMusic(musicRef.current, audioCtxRef?.current);
    };
  }, [soundEnabled, audioCtxRef]);

  // Load background and initialize petals
  useEffect(() => {
    const bgImg = new Image();
    bgImg.onload = () => { bgImageRef.current = bgImg; setBgLoaded(true); };
    bgImg.src = '/images/tea_room_complete.png';
    
    // Initialize cherry blossom petals
    petalRef.current = Array.from({ length: 15 }, () => ({
      x: Math.random() * 600,
      y: Math.random() * 420,
      size: 3 + Math.random() * 6,
      speed: 0.1 + Math.random() * 0.15,
      sway: Math.random() * Math.PI * 2,
      rotation: Math.random() * Math.PI * 2,
      opacity: 0.4 + Math.random() * 0.4,
    }));
  }, []);

  // Draw steam effect
  const drawSteam = useCallback((ctx, x, y, intensity = 1) => {
    const time = frameRef.current;
    for (let i = 0; i < 4; i++) {
      const t = (time + i * 60) % 180;
      const progress = t / 180;
      const alpha = Math.max(0, (0.15 * intensity) - progress * 0.15);
      const scale = 6 + progress * 25;
      const sway = Math.sin(time * 0.008 + i * 1.2) * (4 + progress * 15);
      const rise = progress * 60;

      const grad = ctx.createRadialGradient(x + sway, y - rise, 0, x + sway, y - rise, scale);
      grad.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x + sway, y - rise, scale, 0, Math.PI * 2);
      ctx.fill();
    }
  }, []);

  // Main canvas animation loop (background, petals, steam) - pointer-events: none
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    const animate = () => {
      frameRef.current++;
      ctx.clearRect(0, 0, rect.width, rect.height);
      
      // Draw background
      if (bgImageRef.current) {
        ctx.drawImage(bgImageRef.current, 0, 0, rect.width, rect.height);
      }

      // Draw cherry blossom petals (pointer-events: none layer)
      ctx.save();
      petalRef.current.forEach(p => {
        p.y += p.speed;
        p.rotation += 0.008;
        p.x += Math.sin(p.sway + frameRef.current * 0.006) * 0.15;
        
        if (p.y > rect.height + 10) {
          p.y = -10;
          p.x = Math.random() * rect.width;
        }
        
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = p.opacity;
        
        // Petal shape
        ctx.fillStyle = '#FFB7C5';
        ctx.beginPath();
        ctx.ellipse(0, 0, p.size, p.size / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        
        // Petal highlight
        ctx.fillStyle = 'rgba(255, 220, 230, 0.5)';
        ctx.beginPath();
        ctx.ellipse(-p.size/4, -p.size/6, p.size/3, p.size/4, 0.3, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
      });
      ctx.restore();

      // Draw steam for filled cups
      const currentCups = cupsRef.current;
      currentCups.forEach((cup, i) => {
        if (cup.filled || cup.fillLevel > 0.5) {
          const pos = cupPositions[i];
          const steamX = (pos.x / 100) * rect.width;
          const steamY = (pos.y / 100) * rect.height - 10;
          drawSteam(ctx, steamX, steamY, cup.fillLevel);
        }
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [bgLoaded, drawSteam, cupPositions]);

  // The Ceremony Controller - orchestrates the lift, tilt, pour, return sequence
  const performCeremony = useCallback(async (cupId) => {
    const cup = cups.find(c => c.id === cupId);
    if (ceremonyState !== 'idle' || !cup || cup.filled) return;
    
    const targetCup = cupPositions.find(c => c.id === cupId);
    if (!targetCup) return;
    
    setActiveCup(cupId);
    
    // Phase 1: LIFT & GLIDE - Arc animation using bezier curve
    setCeremonyState('lifting');
    
    // Calculate the arc path - teapot lifts up and arcs to the cup
    const arcHeight = -15; // Lift amount (negative = up)
    const midX = (teapotHome.x + targetCup.x) / 2;
    const midY = Math.min(teapotHome.y, targetCup.y) + arcHeight;
    
    await teapotControls.start({
      x: `${targetCup.x - teapotHome.x}%`,
      y: `${targetCup.y - teapotHome.y - 8}%`, // Position slightly above cup
      rotate: 0,
      transition: {
        duration: 1.2,
        ease: [0.34, 1.56, 0.64, 1], // Custom bezier for arc feel
        type: 'tween',
      }
    });
    
    // Phase 2: TILT - Rotate teapot to pour position
    setCeremonyState('pouring');
    
    // Play pour sound
    if (soundEnabled && audioCtxRef?.current) {
      playPourSound(audioCtxRef.current, 2.5);
    }
    
    // Tilt the teapot
    await teapotControls.start({
      rotate: cupId === 1 ? 35 : cupId === 2 ? -35 : 25, // Tilt toward the cup
      transition: { duration: 0.4, ease: 'easeOut' }
    });
    
    // Phase 3: POUR - Fill the cup gradually
    const fillDuration = 2000; // 2 seconds to fill
    const startTime = Date.now();
    
    const fillInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / fillDuration, 1);
      
      setCups(prev => prev.map(c => 
        c.id === cupId ? { ...c, fillLevel: progress } : c
      ));
      
      if (progress >= 1) {
        clearInterval(fillInterval);
        setCups(prev => prev.map(c => 
          c.id === cupId ? { ...c, filled: true, fillLevel: 1 } : c
        ));
      }
    }, 50);
    
    // Wait for pour to complete
    await new Promise(resolve => setTimeout(resolve, fillDuration));
    
    // Phase 4: RETURN - Tilt back and glide home
    setCeremonyState('returning');
    
    // Tilt back up
    await teapotControls.start({
      rotate: 0,
      transition: { duration: 0.4, ease: 'easeIn' }
    });
    
    // Glide back to center with arc
    await teapotControls.start({
      x: '0%',
      y: '0%',
      transition: {
        duration: 1.0,
        ease: [0.34, 1.56, 0.64, 1],
        type: 'tween',
      }
    });
    
    // Reset state
    setCeremonyState('idle');
    setActiveCup(null);
    
  }, [cups, ceremonyState, teapotControls, soundEnabled, audioCtxRef, cupPositions]);

  // Check completion
  useEffect(() => {
    if (cups.every(c => c.filled) && !complete) {
      setComplete(true);
    }
  }, [cups, complete]);

  // Reset ceremony
  const handleReset = useCallback(() => {
    setCups([
      { id: 1, filled: false, fillLevel: 0 },
      { id: 2, filled: false, fillLevel: 0 },
      { id: 3, filled: false, fillLevel: 0 },
    ]);
    setComplete(false);
    setCeremonyState('idle');
    setActiveCup(null);
    teapotControls.set({ x: '0%', y: '0%', rotate: 0 });
  }, [teapotControls]);

  // Get status message
  const getStatusMessage = () => {
    if (complete) return "The tea is ready. Breathe in the peace...";
    if (ceremonyState === 'lifting') return "Preparing to pour...";
    if (ceremonyState === 'pouring') return "Pouring tea...";
    if (ceremonyState === 'returning') return "Returning teapot...";
    const filledCount = cups.filter(c => c.filled).length;
    if (filledCount > 0) return `${filledCount} of 3 cups filled. Select another...`;
    return "Click a cup to begin the ceremony";
  };

  return (
    <div className="flex flex-col items-center justify-center h-full space-y-4">
      {/* Status message */}
      <motion.div 
        className="text-white/90 text-center font-light italic text-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        key={ceremonyState + complete}
      >
        {getStatusMessage()}
      </motion.div>
      
      {/* Main ceremony container */}
      <div 
        ref={containerRef}
        className="relative rounded-2xl shadow-2xl overflow-hidden bg-stone-800" 
        style={{ width: '600px', height: '420px' }}
      >
        {/* Layer 1: Background canvas (petals + steam) - pointer-events: none */}
        <canvas 
          ref={canvasRef} 
          className="absolute inset-0 w-full h-full pointer-events-none" 
          style={{ zIndex: 1 }}
        />
        
        {/* Layer 2: Animated Teapot */}
        <motion.div
          className="absolute pointer-events-none"
          style={{
            left: `${teapotHome.x}%`,
            top: `${teapotHome.y}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: 15,
            width: '80px',
            height: '60px',
          }}
          animate={teapotControls}
          initial={{ x: '0%', y: '0%', rotate: 0 }}
        >
          {/* Teapot visual */}
          <div className="relative w-full h-full">
            {/* Teapot body */}
            <div 
              className="absolute"
              style={{
                width: '50px',
                height: '40px',
                background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7C 50%, #1E4D5C 100%)',
                borderRadius: '50% 50% 45% 45%',
                left: '15px',
                top: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.2)',
              }}
            >
              {/* Lid */}
              <div 
                style={{
                  position: 'absolute',
                  top: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '30px',
                  height: '10px',
                  background: 'linear-gradient(135deg, #5AA0B4 0%, #3D7D8E 100%)',
                  borderRadius: '50%',
                }}
              />
              {/* Lid knob */}
              <div 
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '8px',
                  height: '8px',
                  background: 'linear-gradient(135deg, #6AB0C4 0%, #4D909E 100%)',
                  borderRadius: '50%',
                }}
              />
            </div>
            
            {/* Spout */}
            <div 
              style={{
                position: 'absolute',
                right: '0px',
                top: '15px',
                width: '20px',
                height: '12px',
                background: 'linear-gradient(90deg, #3D7D8E 0%, #2D6B7C 100%)',
                borderRadius: '0 8px 8px 0',
                transform: 'rotate(-15deg)',
              }}
            />
            
            {/* Handle */}
            <div 
              style={{
                position: 'absolute',
                left: '-5px',
                top: '10px',
                width: '15px',
                height: '30px',
                border: '4px solid #2D6B7C',
                borderRight: 'none',
                borderRadius: '15px 0 0 15px',
              }}
            />
          </div>
          
          {/* Tea stream - only visible when pouring */}
          <AnimatePresence>
            {ceremonyState === 'pouring' && (
              <motion.div
                className="absolute"
                style={{
                  right: '-5px',
                  top: '20px',
                  width: '12px',
                  transformOrigin: 'top center',
                }}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: '45px', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Main stream */}
                <div 
                  className="w-full h-full rounded-b-full"
                  style={{
                    background: 'linear-gradient(180deg, rgba(139, 90, 43, 0.95) 0%, rgba(101, 67, 33, 1) 50%, rgba(80, 52, 25, 0.9) 100%)',
                    boxShadow: '0 4px 8px rgba(60, 35, 15, 0.4)',
                  }}
                />
                {/* Animated droplets */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      width: '5px',
                      height: '7px',
                      background: 'rgba(101, 67, 33, 0.9)',
                      left: `${30 + i * 20}%`,
                    }}
                    animate={{
                      top: ['0px', '50px'],
                      opacity: [0, 1, 0.7],
                    }}
                    transition={{
                      duration: 0.35,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: 'easeIn',
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Layer 3: Interactive cup buttons */}
        {cupPositions.map((pos, i) => {
          const cup = cups[i];
          const isActive = activeCup === pos.id;
          
          return (
            <React.Fragment key={pos.id}>
              {/* Cup fill indicator (tea level) */}
              {cup.fillLevel > 0 && (
                <motion.div
                  className="absolute rounded-full pointer-events-none"
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '24px',
                    height: '16px',
                    background: `linear-gradient(180deg, 
                      rgba(139, 90, 43, ${0.3 + cup.fillLevel * 0.6}) 0%, 
                      rgba(101, 67, 33, ${0.4 + cup.fillLevel * 0.5}) 100%)`,
                    boxShadow: cup.filled ? '0 0 8px rgba(139, 90, 43, 0.5)' : 'none',
                    zIndex: 8,
                  }}
                  initial={{ scale: 0 }}
                  animate={{ 
                    scale: cup.fillLevel,
                    opacity: 0.3 + cup.fillLevel * 0.7 
                  }}
                />
              )}
              
              {/* Clickable cup button */}
              <motion.button
                onClick={() => performCeremony(pos.id)}
                disabled={cup.filled || ceremonyState !== 'idle'}
                className={`absolute rounded-full transition-all outline-none focus:outline-none ${
                  cup.filled 
                    ? 'cursor-default' 
                    : ceremonyState !== 'idle'
                      ? 'cursor-not-allowed opacity-40'
                      : 'cursor-pointer'
                }`}
                style={{ 
                  left: `${pos.x}%`, 
                  top: `${pos.y}%`,
                  width: '32px',
                  height: '24px',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                  background: cup.filled 
                    ? 'rgba(74, 222, 128, 0.3)' 
                    : isActive 
                      ? 'rgba(251, 191, 36, 0.5)'
                      : 'rgba(251, 191, 36, 0.2)',
                  border: cup.filled 
                    ? '2px solid rgba(74, 222, 128, 0.5)' 
                    : '2px solid rgba(251, 191, 36, 0.4)',
                }}
                whileHover={!cup.filled && ceremonyState === 'idle' ? { 
                  scale: 1.2,
                  background: 'rgba(251, 191, 36, 0.4)',
                } : {}}
                whileTap={!cup.filled && ceremonyState === 'idle' ? { scale: 0.95 } : {}}
                aria-label={pos.label}
              >
                {cup.filled && (
                  <motion.div 
                    className="w-2 h-2 bg-green-400 rounded-full mx-auto"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500 }}
                  />
                )}
              </motion.button>
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Reset button */}
      <AnimatePresence>
        {complete && (
          <motion.button 
            onClick={handleReset}
            className="text-white/60 hover:text-white text-sm underline transition-colors"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            Begin again
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeaCeremony;
